import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Bookmark, ChevronDown, LockKeyhole, LogOut, Menu, Settings2, UserRound, X } from "lucide-react";
import { useAuth } from "../AuthContext";
import { COMMUNITY_SECTIONS } from "./Community";

const MAIN_SECTIONS = [
  { to: "/articles", title: "Статьи" },
  { to: "/lectures", title: "Материалы" },
  { to: "/cases", title: "Клинические случаи" },
  { to: "/tools", title: "Инструменты" },
  { to: "/events", title: "События" },
];

function useDisclosure() {
  const [open, setOpen] = useState(false);
  const root = useRef(null);
  const trigger = useRef(null);
  const location = useLocation();
  useEffect(() => { setOpen(false); }, [location]);
  useEffect(() => {
    if (!open) return;
    const outside = event => { if (!root.current?.contains(event.target)) setOpen(false); };
    const escape = event => {
      if (event.key === "Escape") {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);
  return { open, setOpen, root, trigger };
}

function CommunityLinks({ onNavigate }) {
  const { isLoggedIn } = useAuth();
  return COMMUNITY_SECTIONS.map(item => (
    <NavLink key={item.to} to={item.to} className="site-menu-link" onClick={onNavigate}>
      <item.icon size={18} aria-hidden="true" />
      <span>{item.title}</span>
      {!isLoggedIn && <LockKeyhole size={14} aria-label="Требуется вход" />}
    </NavLink>
  ));
}

export function MainNav() {
  const { pathname } = useLocation();
  const mobile = useDisclosure();
  const community = useDisclosure();
  const inCommunity = pathname === "/community" || COMMUNITY_SECTIONS.some(item => pathname === item.to || pathname.startsWith(`${item.to}/`));
  const current = MAIN_SECTIONS.find(item => pathname === item.to || pathname.startsWith(`${item.to}/`))?.title || (inCommunity ? "Сообщество" : pathname === "/" ? "Главная" : "Разделы портала");

  return (
    <nav className="site-navigation" aria-label="Основные разделы" ref={mobile.root}>
      <div className="site-navigation-inner">
        <div className="site-desktop-links">
          {MAIN_SECTIONS.map(item => <NavLink key={item.to} to={item.to} className="site-primary-link">{item.title}</NavLink>)}
          <div className="site-community-dropdown" ref={community.root}>
            <div className={`site-community-trigger${inCommunity ? " active" : ""}`}>
              <NavLink to="/community" className="site-primary-link">Сообщество</NavLink>
              <button type="button" className="site-dropdown-toggle" aria-label="Разделы сообщества" aria-expanded={community.open} aria-controls="community-dropdown" ref={community.trigger} onClick={() => community.setOpen(!community.open)}>
                <ChevronDown size={17} aria-hidden="true" />
              </button>
            </div>
            {community.open && <div id="community-dropdown" className="site-dropdown-panel"><CommunityLinks onNavigate={() => community.setOpen(false)} /></div>}
          </div>
        </div>
        <button type="button" className="site-mobile-menu-toggle" aria-expanded={mobile.open} aria-controls="mobile-site-menu" ref={mobile.trigger} onClick={() => mobile.setOpen(!mobile.open)}>
          {mobile.open ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
          Меню<span className="site-mobile-current">{current}</span>
        </button>
        {mobile.open && (
          <div id="mobile-site-menu" className="site-mobile-panel">
            {MAIN_SECTIONS.map(item => <NavLink key={item.to} to={item.to} className="site-menu-link" onClick={() => mobile.setOpen(false)}>{item.title}</NavLink>)}
            <NavLink to="/community" className="site-menu-link site-mobile-community" onClick={() => mobile.setOpen(false)}>Сообщество</NavLink>
            <div className="site-mobile-community-links"><CommunityLinks onNavigate={() => mobile.setOpen(false)} /></div>
          </div>
        )}
      </div>
    </nav>
  );
}

export function AccountNav({ onLoginRequest }) {
  const { user, isLoggedIn, isEditor, isAdmin, logout } = useAuth();
  const { open, setOpen, root, trigger } = useDisclosure();
  if (!isLoggedIn) return <button type="button" className="header-auth-btn" onClick={onLoginRequest}>Войти</button>;
  return (
    <div className="site-account" ref={root}>
      <button type="button" className="site-account-toggle" aria-label={`Меню профиля: ${user.display_name}`} aria-expanded={open} aria-controls="account-dropdown" ref={trigger} onClick={() => setOpen(!open)}>
        <UserRound size={18} aria-hidden="true" /><span>{user.display_name}</span><ChevronDown size={16} aria-hidden="true" />
      </button>
      {open && (
        <nav id="account-dropdown" className="site-dropdown-panel" aria-label="Личный кабинет">
          <Link className="site-menu-link" to="/profile" onClick={() => setOpen(false)}><UserRound size={18} aria-hidden="true" />Мой профиль</Link>
          <Link className="site-menu-link" to="/saved" onClick={() => setOpen(false)}><Bookmark size={18} aria-hidden="true" />Сохранённое</Link>
          {(isEditor || isAdmin) && <Link className="site-menu-link" to="/admin" onClick={() => setOpen(false)}><Settings2 size={18} aria-hidden="true" />Управление порталом</Link>}
          <button type="button" className="site-menu-link site-logout" onClick={() => { setOpen(false); logout(); }}><LogOut size={18} aria-hidden="true" />Выйти</button>
        </nav>
      )}
    </div>
  );
}
